import { CreateMemento } from '@ui-furniture/common/memento/types/create-memento';

import { Question } from './types/question';
import { QuestionList } from './types/question-list';

export class QuestionnaireRunner<TId extends string> {
  private _currentQuestionId: TId | null = null;
  constructor(
    private readonly _questionList: QuestionList<TId>,
    private readonly _config: {
      onDone?: () => any;
    } = {}
  ) {}

  private _getNextIdFromQuestion = async <TAnswer>(
    question: Question<TAnswer>,
    answer: TAnswer
  ): Promise<TId | null> => {
    const { next } = question;
    if (next === null) return null;

    let nextId: string | null = null;
    if (typeof next === 'string') {
      nextId = next;
    } else {
      nextId = next(answer);
    }

    if (nextId === null) return null;

    if (!this._questionList.list[nextId])
      throw new Error(`There is no question for "${nextId}"`);
    return nextId as TId;
  };

  private _askQuestion = async (id: TId) => {
    this._currentQuestionId = id;
    const question = this._questionList.list[id];

    question.before?.map((before) => before());

    await this._getAndProcessAnswer(id);
  };

  private _getAndProcessAnswer = async (id: TId) => {
    this._currentQuestionId = id;
    const question = this._questionList.list[id];

    const answer = await question.getUserInput();

    question.after?.map((after) => after(answer));

    await this._tryNext(question, answer);
  };

  private _tryNext = async <TAnswer>(
    question: Question<TAnswer>,
    answer: TAnswer
  ) => {
    const nextId = await this._getNextIdFromQuestion(question, answer);

    if (nextId !== null) await this._askQuestion(nextId);
    else this._config.onDone?.();
  };

  start = () => {
    this._askQuestion(this._questionList.startQuestion).catch((err) => {
      throw err;
    });
  };

  createMemento: CreateMemento = () => {
    const savedState = {
      questionId: this._currentQuestionId,
    };
    return {
      restore: () => {
        if (savedState.questionId !== null) {
          this._getAndProcessAnswer(savedState.questionId).catch((err) => {
            throw err;
          });
        }
      },
    };
  };
}
